import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DatePicker } from "@/components/DatePicker";
import { SlotPicker } from "@/components/SlotPicker";
import { ClinicChooser } from "@/components/ClinicChooser";
import { useClinics } from "@/hooks/useClinics";

export const EMPTY_FORM = { clinic: "", name: "", age: "", gender: "", phone: "", email: "", date: "", time: "", reason: "" };
const GENDERS = ["Male", "Female", "Other"];

export const AppointmentFields = ({ form, setForm, prefix, refreshKey, allowAnyDate = false }) => {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const clinic = useClinics().find((c) => c.id === form.clinic);
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      <div className="space-y-2 sm:col-span-2">
        <Label>Appointment type & clinic</Label>
        <ClinicChooser value={form.clinic} onChange={(c) => setForm({ ...form, clinic: c, date: "", time: "" })} prefix={prefix} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label>Select date</Label>
        <DatePicker value={form.date} onChange={(date) => setForm({ ...form, date, time: "" })} testid={`${prefix}-date-picker`} days={clinic?.days} allowPast={allowAnyDate} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label>Select time</Label>
        <SlotPicker clinic={form.clinic} date={form.date} value={form.time} onChange={(time) => setForm({ ...form, time })} refreshKey={refreshKey} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${prefix}-name`}>Patient name</Label>
        <Input id={`${prefix}-name`} className="h-11" placeholder="Full name" value={form.name} onChange={set("name")} required data-testid={`${prefix}-name-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-age`}>Age</Label>
        <Input id={`${prefix}-age`} className="h-11" type="number" min="0" max="120" placeholder="e.g. 32" value={form.age} onChange={set("age")} required data-testid={`${prefix}-age-input`} />
      </div>
      <div className="space-y-2">
        <Label>Gender</Label>
        <div className="flex gap-2">
          {GENDERS.map((g) => (
            <button type="button" key={g} onClick={() => setForm({ ...form, gender: g })} data-testid={`${prefix}-gender-${g.toLowerCase()}`}
              className={`flex-1 h-11 rounded-md border text-sm transition-colors ${form.gender === g ? "bg-primary text-white border-primary" : "bg-white border-slate-200 hover:border-secondary"}`}>{g}</button>
          ))}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-phone`}>Mobile number</Label>
        <Input id={`${prefix}-phone`} className="h-11" inputMode="tel" placeholder="10-digit mobile" value={form.phone} onChange={set("phone")} required data-testid={`${prefix}-phone-input`} />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${prefix}-email`}>Email <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <Input id={`${prefix}-email`} type="email" className="h-11" value={form.email} onChange={set("email")} data-testid={`${prefix}-email-input`} />
      </div>
      <div className="space-y-2 sm:col-span-2">
        <Label htmlFor={`${prefix}-reason`}>Reason for visit <span className="text-muted-foreground font-normal">(optional)</span></Label>
        <Textarea id={`${prefix}-reason`} rows={3} placeholder="e.g. High sugar readings, thyroid follow-up" value={form.reason} onChange={set("reason")} data-testid={`${prefix}-reason-input`} />
      </div>
    </div>
  );
};

export const validateForm = (form) => {
  if (!form.clinic) return "Please choose a clinic or online consultation";
  if (!form.date || !form.time) return "Please choose a date and time slot";
  if (!form.name.trim()) return "Please enter patient name";
  if (form.age === "" || Number(form.age) < 0 || Number(form.age) > 120) return "Enter a valid age";
  if (!form.gender) return "Select gender";
  return null;
};
